import type { CSSProperties } from "react";
import { Footnotes, Sup } from "./Footnotes";
import type { TimelineBlock } from "./model";
import { SlideHeading } from "./SlideHeading";

/** The line runs the width of the stage under the heading; each item gets an equal column, its dot at the centre. */
const LEFT = 64;
const WIDTH = 1152;

/**
 * Points in time on one line, oldest on the left. Revealed in steps, stop k shows items 0..k: the newest is the
 * yellow one, earlier ones stay in grey, later ones wait as faint dots, and the line fills up to the newest. Shown
 * all at once, `emphasis` (if any) is the yellow one. Footnotes follow the yellow item (all items when there is none).
 */
export function TimelineSlide({ block, stop }: { block: TimelineBlock; stop: number }) {
  const n = block.items.length;
  const steps = block.reveal === "steps";
  const shownUpTo = steps ? Math.min(stop, n - 1) : n - 1;
  const hot = steps ? shownUpTo : block.items.findIndex((i) => i.id === block.emphasis);
  const col = WIDTH / n;
  // The filled part of the line ends at the newest shown dot (or runs the whole way when all are shown).
  const fill = steps ? (shownUpTo + 0.5) * col : WIDTH;
  const top = block.lead ? 300 : 260;
  const cited = hot >= 0 ? block.items[hot]!.refs : block.items.flatMap((i) => i.refs);
  const refs = [...new Set([...block.refs, ...cited])].slice(0, 6);

  return (
    <div className="slide">
      <SlideHeading
        blockId={block.id}
        title={block.title}
        lead={
          block.lead && (
            <>
              {block.lead}
              <Sup refs={block.refs} />
            </>
          )
        }
      />
      <div className="abs tl" style={{ left: LEFT, top, width: WIDTH }} data-testid="timeline">
        <div className="tl-line" aria-hidden="true">
          <div className="tl-line-fill" style={{ width: fill }} />
        </div>
        <ol className="tl-items" style={{ "--cols": n } as CSSProperties}>
          {block.items.map((item, i) => {
            const state = i > shownUpTo ? "later" : i === hot ? "hot" : "shown";
            return (
              <li key={item.id} className="tl-item" data-state={state} aria-hidden={state === "later"}>
                <span className="tl-when">{item.when}</span>
                <span className="tl-dot" />
                <span className="tl-label">{item.label}</span>
                {item.caption && (
                  <span className="tl-caption">
                    {item.caption}
                    <Sup refs={item.refs} />
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      <Footnotes refs={refs} />
    </div>
  );
}
